import { useEffect } from 'react';
import { useGcsStore } from '../../store/useGcsStore';
import { useNotificationStore } from '../../store/useNotificationStore';
import { useDialogQueueStore } from '../../store/useDialogQueueStore';
import type { GcsStore } from '../../store/useGcsStore';
import { Connected, Disconnected, type ConnectionState } from '../../types/general/connectionState';
import { DEFAULT_NOTIFICATION_DURATION_MS, InAppNotificationType } from '../../types/ui/inAppNotification';
import { en } from '../../locales/en';
import { getServerNameCode } from '../../config/gcs/gcsConfigs';
import serverConnectionLostSound from '../../assets/sounds/server-connection-lost.mp3';
import serverConnectionRestoredSound from '../../assets/sounds/server-connection-regained.mp3';
import { GcsWebSocketClosureCodes } from '../../services/gcs/GcsWebSocketClosureCodes';
import { soundEffectsService } from '../../services/sound/soundEffectsService';
import { AnotherConnectionDetectedDialog } from '../../components/dialogs/AnotherConnectionDetectedDialog';

export function GcsConnectionStateListener() {
  useEffect(() => {
    const selectConnectionStates = (state: GcsStore): Map<string, ConnectionState | null> => {
      const transitions = new Map<string, ConnectionState | null>();
      state.data.forEach((gcsData, gcsId) => {
        transitions.set(gcsId, gcsData.connectionState ?? null);
      });
      return transitions;
    };

    // Custom equality function to compare Map contents
    const equalityFn = (a: Map<string, ConnectionState | null>, b: Map<string, ConnectionState | null>): boolean => {
      if (a.size !== b.size) return false;
      for (const [key, value] of a) {
        if (b.get(key) !== value) return false;
      }
      return true;
    };

    const unsubscribe = useGcsStore.subscribe(
      selectConnectionStates,
      (nextStates, prevStates = new Map()) => {
        const { showNotification } = useNotificationStore.getState();
        const { showDialog, hideDialog } = useDialogQueueStore.getState();
        nextStates.forEach((nextState, gcsId) => {
          const serverNameCode = getServerNameCode(gcsId);
          const prevState = prevStates.get(gcsId);
          if (prevState instanceof Connected && !(nextState instanceof Connected)) {
            if (
              nextState instanceof Disconnected &&
              (nextState.statusCode == GcsWebSocketClosureCodes.USER_LOGGED_OUT ||
                nextState.statusCode == GcsWebSocketClosureCodes.NO_STATUS_RECEIVED)
            ) {
              // Do nothing
              return;
            }
            soundEffectsService.enqueue(serverConnectionLostSound);
            if (
              nextState instanceof Disconnected &&
              nextState.statusCode == GcsWebSocketClosureCodes.USER_CONNECTED_IN_OTHER_CLIENT
            ) {
              showDialog({
                id: `connection-conflict-${gcsId}`,
                component: <AnotherConnectionDetectedDialog gcsId={gcsId} onClose={hideDialog} />
              });
            } else {
              showNotification({
                type: InAppNotificationType.ERROR,
                body: `${serverNameCode} - ${en.inAppNotifications.serverConnectionLostBody}`,
                durationMs: DEFAULT_NOTIFICATION_DURATION_MS
              });
            }
          } else if (
            !(prevState instanceof Connected) &&
            nextState instanceof Connected &&
            !nextState.isFirstConnection
          ) {
            soundEffectsService.enqueue(serverConnectionRestoredSound);
            showNotification({
              type: InAppNotificationType.SUCCESS,
              body: `${serverNameCode} - ${en.inAppNotifications.serverConnectionRegainedBody}`,
              durationMs: DEFAULT_NOTIFICATION_DURATION_MS
            });
          }
        });
      },
      {
        fireImmediately: true,
        equalityFn
      }
    );

    return unsubscribe;
  }, []);

  return null;
}
